// Native coordination facade (design §5.2).
//
// Wraps the `@aonsoku/capacitor-native/coordination` plugin so the
// `CoordinationManager` can talk to the native WebSocket layer on iOS/Android
// with the same surface area as the TypeScript `CoordinationWsClient`. Web
// falls back to the TS client; Electron supplies a preload-backed native
// implementation. Mirrors the facade pattern in `src/native/audio/facade.ts` and
// `src/native/bridge/facade.ts`.

import {
  AonsokuNativeCoordination,
  type AonsokuNativeCoordinationPlugin,
  COORDINATION_PLUGIN_NAME,
} from "@aonsoku/capacitor-native/coordination";
import type { PluginListenerHandle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import type { StoredDeviceTokens } from "@/coordination/httpClient";
import type { CoordinationConfig } from "@/coordination/tokenStore";
import type {
  CommandResult,
  ConnectionSeq,
  DeviceId,
  Envelope,
  MessageId,
  PlaybackSnapshot,
  RemoteCommand,
  SessionGeneration,
  SessionId,
  SnapshotRevision,
} from "@/coordination/types";
import {
  COORDINATION_PROTOCOL_VERSION,
  CoordinationCapability,
} from "@/coordination/types";
import type {
  ConnectionCallbacks,
  ConnectionState,
  CoordinationClient,
  RefreshGenerationFn,
  SendCommandOptions,
} from "@/coordination/wsClient";
import { getNativeAudioPluginAvailability } from "@/native/audio/facade";
import { getPlaybackCapabilities } from "@/utils/capabilities";

export function isNativeCoordinationPlaybackOwned(): boolean {
  return (
    isNativeCoordinationAvailable() &&
    getPlaybackCapabilities().supportsNativePlayback &&
    getNativeAudioPluginAvailability().available
  );
}

export type NativeCoordinationAvailability =
  | { available: true; plugin: AonsokuNativeCoordinationPlugin }
  | { available: false; reason: string };

const NATIVE_COORDINATION_PLATFORMS = ["ios", "android"];

/// Returns whether the native coordination plugin is available. Mirrors
/// `getNativeAudioPluginAvailability()` — true only when running on a native
/// Capacitor platform with a registered plugin, or Electron with its preload
/// bridge exposed.
export function getNativeCoordinationAvailability(): NativeCoordinationAvailability {
  if (
    typeof window !== "undefined" &&
    window.aonsokuNativeCoordination !== undefined
  ) {
    return { available: true, plugin: window.aonsokuNativeCoordination };
  }

  if (
    !Capacitor.isNativePlatform() ||
    !NATIVE_COORDINATION_PLATFORMS.includes(Capacitor.getPlatform())
  ) {
    return {
      available: false,
      reason: "Only supported on native Capacitor platforms (iOS/Android)",
    };
  }

  if (!Capacitor.isPluginAvailable(COORDINATION_PLUGIN_NAME)) {
    return {
      available: false,
      reason: "Native coordination plugin is not installed",
    };
  }

  return { available: true, plugin: AonsokuNativeCoordination };
}

export function isNativeCoordinationAvailable(): boolean {
  return getNativeCoordinationAvailability().available;
}

function normalizeHeaders(
  headers: HeadersInit | undefined,
): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return headers;
}

function normalizeBody(body: BodyInit | null | undefined): string | undefined {
  if (body === null || body === undefined) return undefined;
  if (typeof body === "string") return body;
  throw new Error("coordination: native HTTP only supports string bodies");
}

export function createNativeCoordinationFetch(
  plugin: AonsokuNativeCoordinationPlugin,
): typeof fetch {
  return async (input, init) => {
    if (typeof input !== "string") {
      throw new Error("coordination: native HTTP requires a URL string");
    }
    const response = await plugin.request({
      url: input,
      method: init?.method,
      headers: normalizeHeaders(init?.headers),
      body: normalizeBody(init?.body),
    });
    const emptyBodyStatus = [204, 205, 304].includes(response.status);
    return new Response(emptyBodyStatus ? null : response.body, {
      status: response.status,
      statusText: response.statusText,
      headers: {
        "Content-Type": "application/json",
      },
    });
  };
}

/// Token/config store backed by the native plugin (Keychain/Keystore +
/// preferences). Implements the same interface as the TS
/// `tokenStore.ts` functions so the manager can swap in the native-backed
/// store when the plugin is available (design §6.3). Only one store is active
/// at a time — the manager picks native or IndexedDB/localStorage, never both.
export class NativeCoordinationTokenStore {
  constructor(private readonly plugin: AonsokuNativeCoordinationPlugin) {}

  async loadTokens(): Promise<StoredDeviceTokens | null> {
    const stored = await this.plugin.loadTokens();
    if (!stored) return null;
    return {
      deviceId: stored.deviceId,
      accountId: stored.accountId,
      accessToken: stored.accessToken,
      refreshToken: stored.refreshToken,
      accessTokenExpiresAt: stored.accessTokenExpiresAt ?? 0,
      historyLimit: stored.historyLimit,
    };
  }

  async saveTokens(tokens: StoredDeviceTokens | null): Promise<void> {
    if (!tokens) {
      await this.plugin.clearTokens();
      return;
    }
    await this.plugin.storeTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      deviceId: tokens.deviceId,
      accountId: tokens.accountId,
      historyLimit: tokens.historyLimit,
    });
  }

  async clearTokens(): Promise<void> {
    await this.plugin.clearTokens();
  }

  async loadConfig(): Promise<CoordinationConfig | null> {
    const cfg = await this.plugin.loadConfig();
    if (!cfg) return null;
    return { serverUrl: cfg.serverUrl, identityUrl: cfg.identityUrl };
  }

  async saveConfig(config: CoordinationConfig | null): Promise<void> {
    if (!config) {
      // The native plugin has no explicit clear-config; overwrite with empty.
      await this.plugin.storeConfig({ serverUrl: "", identityUrl: "" });
      return;
    }
    await this.plugin.storeConfig({
      serverUrl: config.serverUrl,
      identityUrl: config.identityUrl,
    });
  }
}

/// Interface for token/config persistence used by `CoordinationManager`. Both
/// the TS `tokenStore.ts` function set and `NativeCoordinationTokenStore`
/// satisfy it, so the manager can hold a single reference regardless of
/// transport.
export interface CoordinationTokenStore {
  loadTokens(): Promise<StoredDeviceTokens | null>;
  saveTokens(tokens: StoredDeviceTokens | null): Promise<void>;
  clearTokens(): Promise<void>;
  loadConfig(): Promise<CoordinationConfig | null>;
  saveConfig(config: CoordinationConfig | null): Promise<void>;
}

/// §9.1 dedup cache for incoming envelopes. Mirrors the TS client's cache:
/// the server replays the original result for a replayed messageId, so a
/// duplicate here is expected and is skipped silently.
class DedupCache {
  private readonly seen = new Map<MessageId, number>();
  private readonly max: number;
  constructor(max = 200) {
    this.max = max;
  }
  has(id: MessageId): boolean {
    return this.seen.has(id);
  }
  mark(id: MessageId): void {
    if (this.seen.has(id)) return;
    if (this.seen.size >= this.max) {
      const oldest = this.seen.keys().next().value;
      if (oldest !== undefined) this.seen.delete(oldest);
    }
    this.seen.set(id, Date.now());
  }
  clear(): void {
    this.seen.clear();
  }
  size(): number {
    return this.seen.size;
  }
}

/// §9.1 pending-ack entry. Holds the caller's resolve/reject and a timeout
/// timer so the facade can resolve the `sendCommandAck()` promise when the
/// native plugin emits `coordinationAck`.
interface PendingAck {
  messageId: MessageId;
  createdAt: number;
  resolve: (result: CommandResult) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  retry: {
    targetDeviceId: DeviceId;
    expectedGeneration: SessionGeneration;
    command: RemoteCommand;
    options: SendCommandOptions;
    attempted: boolean;
  };
}

const DEFAULT_ACK_TIMEOUT_MS = 10_000;

/// Native coordination client — adapts the native plugin to the
/// `CoordinationClient` surface (design §5.2). Envelope JSON produced by the
/// native plugin mirrors `src/coordination/types.ts` `Envelope`, so
/// `coordinationEvent` payloads are parsed back into `Envelope` and forwarded
/// through `ConnectionCallbacks` — the rest of the manager and store are
/// unaware of which transport is active.
///
/// §9.1 ack/seq/dedup: the facade tracks `lastSeq` on every incoming envelope
/// and submits it in the next `connect()` call's `lastSeq` option. It
/// maintains a dedup cache for incoming `command`/`snapshot_projection`
/// envelopes and a pending-ack map so `sendCommandAck()` can return a
/// `Promise<CommandResult>`. The native plugin emits `coordinationAck` when
/// a `command_ack` envelope arrives; the facade resolves the matching
/// pending entry.
///
/// Reconnect: the native plugin fires `coordinationReconnectNeeded` after an
/// unexpected disconnect (the WebSocket ticket is one-time/expiring so the
/// native layer cannot self-reconnect). The manager registers a reconnect
/// callback via `setReconnectHandler` which re-fetches a ticket via
/// `httpClient.getWsTicket()` and calls `connect()` again.
export class NativeCoordinationClient implements CoordinationClient {
  private state: ConnectionState = "disconnected";
  private deviceId: DeviceId | null;
  private capabilities: number;
  private reconnectHandler: (() => Promise<void>) | null = null;
  private listeners: PluginListenerHandle[] = [];
  private disposed = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  /// §9.2: highest server seq the client has processed. Submitted in the
  /// next `connect()` call's `lastSeq` option.
  private lastSeq: ConnectionSeq = 0;
  /// §9.1 dedup cache for incoming envelopes.
  private dedup = new DedupCache();
  /// §9.1 pending-ack map keyed by messageId.
  private pendingAcks = new Map<MessageId, PendingAck>();
  /// §13 stale-epoch retry hook. Wired by the manager.
  private refreshGenerationFn: RefreshGenerationFn | null = null;

  constructor(
    private readonly plugin: AonsokuNativeCoordinationPlugin,
    private readonly urlFn: () => string,
    private readonly ticketFn: () => Promise<string | null>,
    deviceId: DeviceId | null,
    capabilities: number,
    private readonly callbacks: ConnectionCallbacks,
    private readonly playbackOwned = true,
  ) {
    this.deviceId = deviceId;
    this.capabilities = capabilities;
  }

  /// Register a reconnect handler invoked when the native plugin requests a
  /// fresh ticket (design §6.3, §9 reconnect). The manager re-fetches a ticket
  /// and calls `connect()` again.
  setReconnectHandler(handler: () => Promise<void>): void {
    this.reconnectHandler = handler;
  }

  /// §13: wire the stale-epoch retry hook. The manager passes a function
  /// that resolves to the latest generation for a target device.
  setRefreshGenerationFn(fn: RefreshGenerationFn): void {
    this.refreshGenerationFn = fn;
  }

  /// Test-only: inspect the dedup cache size.
  internalDedupSize(): number {
    return this.dedup.size();
  }

  /// Test-only: inspect the pending-ack map size.
  internalPendingAckSize(): number {
    return this.pendingAcks.size;
  }

  /// Test-only: current tracked lastSeq.
  internalLastSeq(): ConnectionSeq {
    return this.lastSeq;
  }

  getState(): ConnectionState {
    return this.state;
  }

  async connect(): Promise<void> {
    if (this.disposed) return;
    if (this.state === "connecting" || this.state === "connected") return;
    this.setState("connecting");

    try {
      const ticket = await this.ticketFn();
      if (this.disposed) return;
      if (!ticket) throw new Error("no ws ticket available");
      await this.attachListeners();
      if (this.disposed) return;
      await this.plugin.connect({
        wsUrl: this.urlFn(),
        ticket,
        deviceId: this.deviceId ?? "",
        capabilities: this.capabilities,
        protocolVersion: COORDINATION_PROTOCOL_VERSION,
        lastSeq: this.lastSeq,
      });
    } catch (err) {
      if (this.disposed) return;
      this.callbacks.onError(
        "internal",
        err instanceof Error ? err.message : "native connect failed",
      );
      this.scheduleRetry();
    }
  }

  disconnect(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    for (const handle of this.listeners) {
      handle.remove().catch(() => {});
    }
    this.listeners = [];
    // Reject in-flight acks so callers don't hang on teardown.
    for (const pending of this.pendingAcks.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error("coordination: disconnected before ack"));
    }
    this.pendingAcks.clear();
    // The dedup cache is per-connection; clear it so a reconnect does not
    // falsely skip messages from the new connection.
    this.dedup.clear();
    this.plugin.disconnect().catch(() => {});
    this.setState("disconnected");
  }

  publishSnapshot(
    sessionId: string,
    generation: SessionGeneration,
    snapshotRevision: SnapshotRevision,
    snapshot: PlaybackSnapshot,
  ): void {
    this.plugin
      .publishSnapshot({
        sessionId,
        generation,
        snapshotRevision,
        snapshotJson: JSON.stringify(snapshot),
      })
      .catch(() => {});
  }

  sendCommand(
    targetDeviceId: DeviceId,
    expectedGeneration: SessionGeneration,
    command: RemoteCommand,
  ): void {
    this.plugin
      .sendCommand({
        targetDeviceId,
        expectedGeneration,
        commandJson: JSON.stringify(command),
      })
      .catch(() => {});
  }

  /// §9.1 ack overload: sends the command and resolves with the
  /// `CommandResult` when the native plugin emits `coordinationAck` for
  /// the matching messageId. Rejects after the timeout. Supports a single
  /// stale-epoch retry when `options.retryOnStaleEpoch` is true.
  sendCommandAck(
    targetDeviceId: DeviceId,
    expectedGeneration: SessionGeneration,
    command: RemoteCommand,
    options: SendCommandOptions,
  ): Promise<CommandResult> {
    const messageId = crypto.randomUUID();
    const retry: PendingAck["retry"] = {
      targetDeviceId,
      expectedGeneration,
      command,
      options,
      attempted: false,
    };
    const promise = this.trackPendingAck(messageId, retry, options);
    this.plugin
      .sendCommand({
        targetDeviceId,
        expectedGeneration,
        commandJson: JSON.stringify(command),
        messageId,
      })
      .catch((err) => {
        const pending = this.pendingAcks.get(messageId);
        if (pending) {
          clearTimeout(pending.timer);
          this.pendingAcks.delete(messageId);
          pending.reject(
            err instanceof Error ? err : new Error("native sendCommand failed"),
          );
        }
      });
    return promise;
  }

  /// Register a pending-ack entry with a timeout timer.
  private trackPendingAck(
    messageId: MessageId,
    retry: PendingAck["retry"],
    options: SendCommandOptions,
  ): Promise<CommandResult> {
    return new Promise<CommandResult>((resolve, reject) => {
      const timeoutMs = options.timeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
      const timer = setTimeout(() => {
        this.pendingAcks.delete(messageId);
        reject(new Error(`command_ack timeout after ${timeoutMs}ms`));
      }, timeoutMs);
      this.pendingAcks.set(messageId, {
        messageId,
        createdAt: Date.now(),
        resolve,
        reject,
        timer,
        retry,
      });
    });
  }

  /// Resend a command with an updated expectedGeneration (stale-epoch
  /// retry, §13). Reuses the original `PendingAck`'s resolve/reject so the
  /// caller's promise resolves/rejects with the retry result. A new
  /// messageId is generated and a fresh timeout timer is armed.
  private resendCommand(
    pending: PendingAck,
    newGeneration: SessionGeneration,
  ): void {
    const messageId = crypto.randomUUID();
    const timeoutMs = pending.retry.options.timeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
    pending.timer = setTimeout(() => {
      this.pendingAcks.delete(messageId);
      pending.reject(new Error(`command_ack timeout after ${timeoutMs}ms`));
    }, timeoutMs);
    this.pendingAcks.set(messageId, pending);
    this.plugin
      .sendCommand({
        targetDeviceId: pending.retry.targetDeviceId,
        expectedGeneration: newGeneration,
        commandJson: JSON.stringify(pending.retry.command),
        messageId,
      })
      .catch(() => {});
  }

  sendActiveControlCommand(command: RemoteCommand): void {
    this.plugin
      .sendActiveControlCommand({
        commandJson: JSON.stringify(command),
      })
      .catch(() => {});
  }

  requestHandoffCandidate(
    sourceDeviceId: DeviceId,
    expectedGeneration: SessionGeneration,
    expectedSnapshotRevision: SnapshotRevision,
  ): void {
    this.plugin
      .requestHandoffCandidate(
        sourceDeviceId,
        expectedGeneration,
        expectedSnapshotRevision,
      )
      .catch(() => {});
  }

  requestHandoffCandidateFromCache(sourceDeviceId: DeviceId): void {
    this.plugin
      .requestHandoffCandidateFromCache({ sourceDeviceId })
      .catch(() => {});
  }

  sendTargetReady(
    transactionId: string,
    generation: SessionGeneration,
    snapshotRevision: SnapshotRevision,
    sourceDeviceId?: DeviceId | null,
    sessionId?: SessionId | null,
  ): void {
    if (!sourceDeviceId || !sessionId) return;
    this.plugin
      .sendTargetReady(
        transactionId,
        generation,
        snapshotRevision,
        sourceDeviceId,
        sessionId,
      )
      .catch(() => {});
  }

  sendRelinquishAck(transactionId: string, snapshot: PlaybackSnapshot): void {
    this.plugin
      .sendRelinquishAck({
        transactionId,
        snapshotJson: JSON.stringify(snapshot),
      })
      .catch(() => {});
  }

  sendControlSessionBegin(targetDeviceId: DeviceId): void {
    this.plugin.sendControlSessionBegin({ targetDeviceId }).catch(() => {});
  }

  sendControlSessionEnd(): void {
    this.plugin.sendControlSessionEnd().catch(() => {});
  }

  requestSnapshots(): void {
    this.plugin.requestSnapshots().catch(() => {});
  }

  private async attachListeners(): Promise<void> {
    if (this.listeners.length > 0) return;
    // `coordinationEvent` — incoming envelope JSON.
    const eventHandle = await this.plugin.addListener(
      "coordinationEvent",
      (data: { envelopeJson: string }) => {
        try {
          const env = JSON.parse(data.envelopeJson) as Envelope;
          this.handleEnvelope(env);
        } catch {
          // Malformed message; ignore (mirrors wsClient behavior).
        }
      },
    );
    if (!this.keepListener(eventHandle)) return;

    // §9.1 `coordinationAck` — emitted by the native plugin when a
    // `command_ack` envelope arrives, so the facade can resolve the
    // pending `sendCommandAck()` promise. The native plugin also tracks
    // lastSeq/dedup on its side; this event is the bridge back to the
    // WebView-side promise.
    const ackHandle = await this.plugin.addListener(
      "coordinationAck",
      (data: { messageId: string; resultJson: string }) => {
        const pending = this.pendingAcks.get(data.messageId);
        if (!pending) return;
        let result: CommandResult;
        try {
          result = JSON.parse(data.resultJson) as CommandResult;
        } catch {
          // Malformed ack payload — reject the pending promise so the
          // caller does not hang.
          clearTimeout(pending.timer);
          this.pendingAcks.delete(data.messageId);
          pending.reject(new Error("coordination: malformed ack result"));
          return;
        }
        // §13 stale-epoch retry path.
        if (
          result.status === "error" &&
          result.code === "stale_epoch" &&
          pending.retry.options.retryOnStaleEpoch &&
          !pending.retry.attempted &&
          this.refreshGenerationFn
        ) {
          clearTimeout(pending.timer);
          this.pendingAcks.delete(data.messageId);
          pending.retry.attempted = true;
          this.refreshGenerationFn(pending.retry.targetDeviceId)
            .then((gen) => {
              if (gen === null) {
                pending.reject(
                  new Error("stale_epoch retry: could not refresh generation"),
                );
                return;
              }
              this.resendCommand(pending, gen);
            })
            .catch((err) => pending.reject(err));
          return;
        }
        // §13: a second stale_epoch after a retry was attempted rejects.
        if (
          result.status === "error" &&
          result.code === "stale_epoch" &&
          pending.retry.attempted
        ) {
          clearTimeout(pending.timer);
          this.pendingAcks.delete(data.messageId);
          pending.reject(
            new Error("stale_epoch: generation still stale after retry"),
          );
          return;
        }
        clearTimeout(pending.timer);
        this.pendingAcks.delete(data.messageId);
        pending.resolve(result);
      },
    );
    if (!this.keepListener(ackHandle)) return;

    // `coordinationStateChange` — connection state updates from the native
    // layer. Forwarded through the same `ConnectionCallbacks`.
    const stateHandle = await this.plugin.addListener(
      "coordinationStateChange",
      (data: { state: ConnectionState; deviceId: string | null }) => {
        this.deviceId = data.deviceId ?? this.deviceId;
        this.setState(data.state);
      },
    );
    if (!this.keepListener(stateHandle)) return;

    // `coordinationReconnectNeeded` — the native layer cannot self-reconnect
    // (single-use ticket), so it asks the WebView to fetch a fresh ticket and
    // call `connect()` again (§6.3).
    const reconnectHandle = await this.plugin.addListener(
      "coordinationReconnectNeeded",
      (_data: { attempt: number }) => {
        if (this.disposed) return;
        this.setState("reconnecting");
        if (this.reconnectHandler) {
          this.reconnectHandler().catch(() => {});
        }
      },
    );
    this.keepListener(reconnectHandle);
  }

  private keepListener(handle: PluginListenerHandle): boolean {
    if (this.disposed) {
      handle.remove().catch(() => {});
      return false;
    }
    this.listeners.push(handle);
    return true;
  }

  private handleEnvelope(env: Envelope): void {
    if (this.disposed) return;
    // §9.2: track the incoming seq on every envelope so the next `connect()`
    // can submit `lastSeq` and the server can skip already-delivered messages.
    if (typeof env.seq === "number" && env.seq > this.lastSeq) {
      this.lastSeq = env.seq;
    }

    // §9.1: dedup incoming command/snapshot envelopes by messageId.
    if (
      (env.type === "command" || env.type === "snapshot_projection") &&
      env.messageId
    ) {
      if (this.dedup.has(env.messageId)) {
        return;
      }
      this.dedup.mark(env.messageId);
    }

    switch (env.type) {
      case "welcome":
        if (env.deviceId) this.deviceId = env.deviceId;
        if (typeof env.seq === "number" && env.seq > this.lastSeq) {
          this.lastSeq = env.seq;
        }
        this.callbacks.onWelcome(
          env.deviceId,
          env.connectionId ?? "",
          env.negotiated ?? CoordinationCapability.NONE,
        );
        break;
      case "heartbeat_ack":
        // No action needed (mirrors wsClient).
        break;
      case "devices_changed":
        this.callbacks.onDevicesChanged(env.devices);
        break;
      case "snapshot_projection":
        this.callbacks.onSnapshotProjection(env);
        break;
      case "command":
        // Playback control is native-owned on mobile. If an older native layer
        // still bridges this envelope, do not reintroduce WebView execution.
        if (!this.playbackOwned) this.callbacks.onCommand(env);
        break;
      case "command_ack":
        // The native plugin emits `coordinationAck` separately; the envelope
        // is handled by the ack listener, not here.
        break;
      case "handoff_candidate":
        // Handoff prepare is native-owned on mobile.
        if (!this.playbackOwned) this.callbacks.onHandoffCandidate(env);
        break;
      case "prepare_relinquish":
        // Source relinquish is native-owned on mobile.
        if (!this.playbackOwned) this.callbacks.onPrepareRelinquish(env);
        break;
      case "handoff_committed":
        // Native playback is already applied; UI consumers need completion.
        this.callbacks.onHandoffCommitted(env);
        break;
      case "handoff_failed":
        this.callbacks.onHandoffFailed(env);
        break;
      case "session_superseded":
        // Playback teardown is native-owned on mobile.
        if (!this.playbackOwned) this.callbacks.onSessionSuperseded(env);
        break;
      case "error":
        this.callbacks.onError(env.code, env.reason);
        break;
      case "capability_disabled":
        this.callbacks.onError(
          "protocol_incompatible",
          `feature disabled: ${env.feature}`,
        );
        break;
      default:
        // heartbeat / snapshot / hello / relinquish_ack /
        // handoff_candidate_request / target_ready — outbound or ignored.
        break;
    }
  }

  private setState(state: ConnectionState): void {
    if (this.disposed && state !== "disconnected") return;
    if (state === "connected") {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.retryAttempt = 0;
    }
    if (this.state === state) return;
    this.state = state;
    this.callbacks.onStateChange(state);
  }

  private scheduleRetry(): void {
    if (this.disposed || this.retryTimer) return;
    this.setState("reconnecting");
    const delay = Math.min(
      30_000,
      1000 * 2 ** Math.min(this.retryAttempt++, 5),
    );
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect().catch(() => this.scheduleRetry());
    }, delay);
  }
}

export type {
  AonsokuNativeCoordinationPlugin,
  CoordinationAckEvent,
  CoordinationCommandOptions,
  CoordinationConfigOptions,
  CoordinationConnectOptions,
  CoordinationHandoffOptions,
  CoordinationHttpRequestOptions,
  CoordinationHttpResponse,
  CoordinationSnapshotOptions,
  CoordinationStateResult,
  CoordinationTokenOptions,
} from "@aonsoku/capacitor-native/coordination";
export {
  AonsokuNativeCoordination,
  COORDINATION_PLUGIN_NAME,
} from "@aonsoku/capacitor-native/coordination";
